import React from "react";
import moment from "moment";
import { IconCheck, IconClock, IconEye, IconLock, IconX } from "@tabler/icons-react";

import { REQUEST_STATUS_META, departmentLabel, refLabel } from "./changeRequestConfig";

const formatTime = (value) => (value ? moment(value).format("DD MMM YYYY, HH:mm") : "—");

const STEP_ICON = {
  APPROVED: IconCheck,
  REJECTED: IconX,
  PENDING: IconClock,
  WAITING: IconLock,
};

/** What a step is waiting for, or what was decided on it. */
const stepDetail = (step, steps) => {
  if (step.status === "APPROVED") {
    return `Approved${step.acted_by?.username ? ` by ${step.acted_by.username}` : ""}`;
  }
  if (step.status === "REJECTED") {
    return `Reason: ${step.reason || "—"}`;
  }
  if (step.status === "PENDING") {
    return "Awaiting";
  }
  const blocking = steps.find((other) => other.step_no < step.step_no && other.status !== "APPROVED");
  return blocking ? `Waiting for ${blocking.label}` : "Waiting";
};

/** The approver boxes, in order, with an arrow between them. */
export const ApprovalSteps = ({ steps }) => {
  if (!steps?.length) {
    return <div className="cr-chain-none">No approval needed</div>;
  }
  return (
    <div className="cr-chain">
      {steps.map((step, index) => {
        const Icon = STEP_ICON[step.status] || IconClock;
        return (
          <React.Fragment key={step.step_no}>
            {index > 0 && <span className="cr-chain-arrow">→</span>}
            <div className={`cr-chain-step cr-chain-${step.status.toLowerCase()}`}>
              <div className="cr-chain-role">
                <Icon stroke={2} size={13} />
                <span>{step.label}</span>
              </div>
              <div className="cr-chain-detail">{stepDetail(step, steps)}</div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const RequestStatusBadge = ({ status }) => {
  const meta = REQUEST_STATUS_META[status] || REQUEST_STATUS_META.PENDING;
  return <span className={`cr-status ${meta.className}`}>{meta.label}</span>;
};

/**
 * Board card: which record, who asked, when, and where the request sits in the
 * approver chain. Not draggable — status only changes through approve / reject.
 */
function ChangeRequestCard({ request, tone, onOpen }) {
  const open = () => onOpen(request);

  return (
    <div
      className={`cr-card${request.can_act ? " cr-card-action" : ""}`}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          open();
        }
      }}
    >
      <div className="cr-card-line">
        <span className="cr-label">{refLabel(request)}:</span>
        <span className={`cr-ref cr-ref-${tone}`}>{request.module_ref}</span>
      </div>

      <div className="cr-card-line cr-card-status">
        <span className="cr-label">Request Status:</span>
        <RequestStatusBadge status={request.status} />
      </div>

      <div className="cr-card-grid">
        <div>
          <div className="cr-field-label">Requestee</div>
          <div className="cr-value">
            {request.requester?.name || request.requester?.username || "—"}
          </div>
        </div>
        <div>
          <div className="cr-field-label">Requested</div>
          <div className="cr-value">
            {request.change_count} field{request.change_count === 1 ? "" : "(s)"}
          </div>
        </div>
        <div>
          <div className="cr-field-label">Type</div>
          <div className="cr-value">
            {(request.product_type && departmentLabel(request.product_type)) ||
              request.order_payment_type ||
              request.requester_department ||
              "—"}
          </div>
        </div>
        <div>
          <div className="cr-field-label">Requested On</div>
          <div className="cr-value cr-value-accent">{formatTime(request.requested_at)}</div>
        </div>
      </div>

      <div className="cr-chain-title">Approvers chain</div>
      <ApprovalSteps steps={request.steps} />

      <div className="cr-card-foot">
        <IconEye stroke={1.5} size={18} />
        <span>{request.can_act ? "Review" : "View"}</span>
      </div>
    </div>
  );
}

export default ChangeRequestCard;
